'use client';

import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { Play, Database, Loader2 } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type {
  PostsPerHourData,
  TopLanguagesData,
  TopDomainsData,
  ContentStatsData,
  CustomQueryResponse,
  AnalyticsStatus,
} from '@/types';
import {
  getPostsPerHour,
  getTopLanguages,
  getTopDomains,
  getContentStats,
  getAnalyticsStatus,
  runCustomQuery,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

// Skeleton loader for bar charts
function BarSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2 animate-pulse">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-12 h-4 bg-border rounded" />
          <div className="flex-1 h-6 bg-border rounded-base" />
          <div className="w-12 h-4 bg-border rounded" />
        </div>
      ))}
    </div>
  );
}

// Skeleton for the area chart
function ChartSkeleton() {
  return (
    <div className="h-64 flex items-center justify-center bg-background/50 rounded-base border-2 border-border border-dashed">
      <div className="text-center text-foreground/50">
        <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin" />
        <p className="text-sm">Loading chart...</p>
      </div>
    </div>
  );
}

// Skeleton for stats grid
function StatsSkeleton({
  className = 'grid grid-cols-2 gap-4',
  items = 4,
}: {
  className?: string;
  items?: number;
}) {
  return (
    <div className={`${className} animate-pulse`}>
      {Array.from({ length: items }).map((_, i) => (
        <div key={i} className="bg-background border-2 border-border rounded-base p-4 text-center">
          <div className="h-8 w-16 bg-border rounded mx-auto mb-2" />
          <div className="h-3 w-20 bg-border rounded mx-auto" />
        </div>
      ))}
    </div>
  );
}

function smoothPostsPerHour(data: PostsPerHourData[], windowSize: number): PostsPerHourData[] {
  if (windowSize <= 1) return data;
  const smoothed: PostsPerHourData[] = [];
  for (let i = 0; i < data.length; i++) {
    const start = Math.max(0, i - windowSize + 1);
    let sum = 0;
    for (let j = start; j <= i; j++) {
      sum += data[j].count;
    }
    const avg = sum / (i - start + 1);
    smoothed.push({ ...data[i], count: Math.round(avg) });
  }
  return smoothed;
}

export function AnalyticsPanel() {
  const searchParams = useSearchParams();
  const [postsPerHour, setPostsPerHour] = useState<PostsPerHourData[]>([]);
  const [topLanguages, setTopLanguages] = useState<TopLanguagesData[]>([]);
  const [topDomains, setTopDomains] = useState<TopDomainsData[]>([]);
  const [contentStats, setContentStats] = useState<ContentStatsData | null>(null);
  const [analyticsStatus, setAnalyticsStatus] = useState<AnalyticsStatus | null>(null);

  const [loadingChart, setLoadingChart] = useState(true);
  const [loadingLangs, setLoadingLangs] = useState(true);
  const [loadingDomains, setLoadingDomains] = useState(true);
  const [loadingContentStats, setLoadingContentStats] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState(true);

  // Custom query state
  const [customQuery, setCustomQuery] = useState("SELECT COUNT(*) as total FROM iceberg_scan('s3://bsky-looper-demo/iceberg/dray/bsky.firehose.posts.jan9')");
  const [queryResult, setQueryResult] = useState<CustomQueryResponse | null>(null);
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryError, setQueryError] = useState<string | null>(null);
  const smoothingDisabled = searchParams?.has('nosmooth') ?? false;
  const postsPerHourChartData = useMemo(() => {
    const sorted = [...postsPerHour].sort(
      (a, b) => new Date(a.hour).getTime() - new Date(b.hour).getTime()
    );
    const trimmed = sorted.slice(-24);
    const data = smoothingDisabled ? trimmed : smoothPostsPerHour(trimmed, 5);
    return data.map((item) => ({
      ...item,
      time: new Date(item.hour).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        hour12: true,
      }),
    }));
  }, [postsPerHour, smoothingDisabled]);

  // Load data independently so cards populate as they finish
  useEffect(() => {
    let cancelled = false;
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const retryDelayMs = 10000;
    const maxAttempts = 6;

    const shouldRetry = (err: unknown) => {
      const message = err instanceof Error ? err.message : String(err);
      return message.includes('cache warming') || message.includes('503');
    };

    const runWithRetry = async <T,>(
      fn: () => Promise<T>,
      onSuccess: (value: T) => void,
      onDone: () => void,
      attempt = 1
    ) => {
      try {
        const result = await fn();
        if (cancelled) return;
        onSuccess(result);
        onDone();
      } catch (err) {
        if (cancelled) return;
        if (shouldRetry(err) && attempt < maxAttempts) {
          const id = setTimeout(() => {
            void runWithRetry(fn, onSuccess, onDone, attempt + 1);
          }, retryDelayMs);
          timeouts.push(id);
          return;
        }
        onDone();
      }
    };

    void runWithRetry(getPostsPerHour, setPostsPerHour, () => setLoadingChart(false));
    void runWithRetry(getTopLanguages, setTopLanguages, () => setLoadingLangs(false));
    void runWithRetry(getTopDomains, setTopDomains, () => setLoadingDomains(false));
    void runWithRetry(getContentStats, setContentStats, () => setLoadingContentStats(false));
    void runWithRetry(getAnalyticsStatus, setAnalyticsStatus, () => setLoadingStatus(false));

    return () => {
      cancelled = true;
      timeouts.forEach(clearTimeout);
    };
  }, []);

  const handleRunQuery = async () => {
    if (!customQuery.trim()) return;

    setQueryLoading(true);
    setQueryError(null);

    try {
      const result = await runCustomQuery({ query: customQuery });
      setQueryResult(result);
    } catch (err) {
      setQueryError(err instanceof Error ? err.message : 'Query failed');
    } finally {
      setQueryLoading(false);
    }
  };

  const maxLangCount = Math.max(...topLanguages.map((d) => d.count), 1);
  const maxDomainCount = Math.max(...topDomains.map((d) => d.count), 1);
  const iceberg = analyticsStatus?.iceberg;
  const vex = analyticsStatus?.vex;
  const documentsIndexed = vex?.documents_indexed;

  const formatCount = (value?: number) => {
    if (value === undefined || value === null) return '—';
    return value.toLocaleString();
  };

  const formatBytes = (value?: number) => {
    if (value === undefined || value === null) return '—';
    if (value === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = value;
    let idx = 0;
  while (size >= 1024 && idx < units.length - 1) {
      size /= 1024;
      idx += 1;
    }
    return `${size.toFixed(size < 10 && idx > 0 ? 1 : 0)} ${units[idx]}`;
  };

  return (
    <div className="p-6 overflow-y-auto h-full min-h-0">
      <div className="flex items-center gap-3 mb-6">
        <h2 className="text-xl font-heading text-foreground">Analytics Dashboard</h2>
        <Badge variant="neutral">
          <Database className="w-3 h-3" />
          Powered by DuckDB
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Posts Per Hour Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Posts Per Hour (Last 24h)</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingChart ? (
              <ChartSkeleton />
            ) : postsPerHour.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-foreground/50">
                No data available
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={postsPerHourChartData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <XAxis
                      dataKey="time"
                      tick={{ fontSize: 11, fill: 'currentColor', fontWeight: 500 }}
                      tickLine={false}
                      axisLine={{ stroke: '#000', strokeWidth: 2 }}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: 'currentColor', fontWeight: 500 }}
                      tickLine={false}
                      axisLine={{ stroke: '#000', strokeWidth: 2 }}
                      tickFormatter={(value) =>
                        value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value
                      }
                      width={45}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '2px solid #000',
                        borderRadius: 'var(--border-radius)',
                        fontSize: '12px',
                        fontWeight: 500,
                        boxShadow: '4px 4px 0px 0px #000',
                      }}
                      formatter={(value) => [value?.toLocaleString() ?? '0', 'Posts']}
                      labelFormatter={(label) => `Hour: ${label}`}
                      cursor={{ fill: 'rgba(0,0,0,0.1)' }}
                    />
                    <Bar
                      dataKey="count"
                      fill="oklch(79.76% 0.2044 153.08)"
                      stroke="#000"
                      strokeWidth={2}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Content Stats */}
        <Card>
          <CardHeader>
            <CardTitle>Content Stats (Last 24h)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                {loadingContentStats ? (
                  <StatsSkeleton className="grid grid-cols-2 lg:grid-cols-3 gap-4" items={3} />
                ) : !contentStats ? (
                  <div className="h-32 flex items-center justify-center text-foreground/50">
                    No data available
                  </div>
                ) : (
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-background border-2 border-border rounded-base p-4 text-center">
                      <div className="text-2xl font-heading text-foreground">
                        {contentStats.total_posts.toLocaleString()}
                      </div>
                      <div className="text-xs text-foreground/70 mt-1 font-base">Total Posts</div>
                    </div>
                    <div className="bg-chart-1/20 border-2 border-border rounded-base p-4 text-center">
                      <div className="text-2xl font-heading text-foreground">
                        {contentStats.media_percentage.toFixed(1)}%
                      </div>
                      <div className="text-xs text-foreground/70 mt-1 font-base">Media Posts</div>
                    </div>
                    <div className="bg-chart-5/20 border-2 border-border rounded-base p-4 text-center">
                      <div className="text-2xl font-heading text-foreground">
                        {contentStats.links_percentage.toFixed(1)}%
                      </div>
                      <div className="text-xs text-foreground/70 mt-1 font-base">Link Posts</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t-2 border-border pt-4">
                <div className="text-xs font-heading text-foreground/70 mb-3">
                  Post Length Distribution
                </div>
                {loadingContentStats ? (
                  <StatsSkeleton className="grid grid-cols-2 lg:grid-cols-3 gap-4" items={3} />
                ) : (contentStats?.post_lengths?.length ?? 0) === 0 ? (
                  <div className="h-24 flex items-center justify-center text-foreground/50">
                    No data available
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    {(() => {
                      const postLengthData = contentStats?.post_lengths ?? [];
                      const total = postLengthData.reduce((sum, d) => sum + d.count, 0);
                      const labels: Record<string, { label: string; desc: string; bg: string }> = {
                        short: { label: 'Short', desc: '<50 chars', bg: 'bg-chart-1/20' },
                        medium: { label: 'Medium', desc: '50-250', bg: 'bg-chart-2/20' },
                        long: { label: 'Long', desc: '>250', bg: 'bg-chart-4/20' },
                      };
                      const byBucket = new Map(postLengthData.map((item) => [item.bucket, item.count]));
                      const ordered = ['short', 'medium', 'long'];
                      return ordered.map((bucket) => {
                        const meta = labels[bucket];
                        const count = byBucket.get(bucket) ?? 0;
                        const percentage = total > 0 ? (count / total) * 100 : 0;
                        return (
                          <div key={bucket} className={`${meta.bg} border-2 border-border rounded-base p-4 text-center`}>
                            <div className="text-2xl font-heading text-foreground">
                              {percentage.toFixed(1)}%
                            </div>
                            <div className="text-xs text-foreground/70 mt-1 font-base">
                              {meta.label} <span className="text-foreground/50 font-base">{meta.desc}</span>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top Domains */}
        <Card>
          <CardHeader>
            <CardTitle>Top Link Domains</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingDomains ? (
              <BarSkeleton rows={10} />
            ) : topDomains.length === 0 ? (
              <div className="h-32 flex items-center justify-center text-foreground/50">
                No data available
              </div>
            ) : (
              <div className="space-y-2">
                {topDomains.slice(0, 10).map((item) => (
                  <div key={item.domain} className="flex items-center gap-2">
                    <span className="text-xs text-foreground flex-1 truncate font-mono">
                      {item.domain}
                    </span>
                    <div className="w-32 h-6 bg-background border-2 border-border rounded-base overflow-hidden flex-shrink-0">
                      <div
                        className="h-full bg-chart-5 transition-all duration-500"
                        style={{ width: `${(item.count / maxDomainCount) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-foreground w-12 text-right font-mono">
                      {item.count.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Languages */}
        <Card>
          <CardHeader>
            <CardTitle>Top Languages</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingLangs ? (
              <BarSkeleton rows={10} />
            ) : topLanguages.length === 0 ? (
              <div className="h-32 flex items-center justify-center text-foreground/50">
                No data available
              </div>
            ) : (
              <div className="space-y-2">
                {topLanguages.slice(0, 10).map((item) => (
                  <div key={item.language} className="flex items-center gap-2">
                    <span className="text-xs text-foreground font-heading w-12 flex-shrink-0">
                      {item.language || 'unknown'}
                    </span>
                    <div className="flex-1 h-6 bg-background border-2 border-border rounded-base overflow-hidden">
                      <div
                        className="h-full bg-chart-4 transition-all duration-500"
                        style={{ width: `${(item.count / maxLangCount) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-foreground w-16 text-right">
                      {item.percentage.toFixed(1)}%
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Iceberg Stats */}
        <Card>
          <CardHeader>
            <CardTitle>Iceberg Table Stats</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingStatus ? (
              <StatsSkeleton className="grid grid-cols-2 lg:grid-cols-3 gap-4" items={6} />
            ) : !iceberg ? (
              <div className="h-32 flex items-center justify-center text-foreground/50">
                {analyticsStatus?.iceberg_error ? analyticsStatus.iceberg_error : 'No data available'}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-background border-2 border-border rounded-base p-4 text-center">
                    <div className="text-2xl font-heading text-foreground">
                      {iceberg.total_data_files.toLocaleString()}
                    </div>
                    <div className="text-xs text-foreground/70 mt-1 font-base">Parquet Files</div>
                  </div>
                  <div className="bg-background border-2 border-border rounded-base p-4 text-center">
                    <div className="text-2xl font-heading text-foreground">
                      {iceberg.total_records.toLocaleString()}
                    </div>
                    <div className="text-xs text-foreground/70 mt-1 font-base">Total Records</div>
                  </div>
                  <div className="bg-background border-2 border-border rounded-base p-4 text-center">
                    <div className="text-2xl font-heading text-foreground">
                      {formatBytes(iceberg.total_file_size_bytes)}
                    </div>
                    <div className="text-xs text-foreground/70 mt-1 font-base">Total Size</div>
                  </div>
                  <div className="bg-background border-2 border-border rounded-base p-4 text-center">
                    <div className="text-2xl font-heading text-foreground">
                      {formatBytes(iceberg.avg_file_size_bytes)}
                    </div>
                    <div className="text-xs text-foreground/70 mt-1 font-base">Avg File Size</div>
                  </div>
                  <div className="bg-background border-2 border-border rounded-base p-4 text-center">
                    <div className="text-2xl font-heading text-foreground">
                      {iceberg.avg_records_per_file.toLocaleString()}
                    </div>
                    <div className="text-xs text-foreground/70 mt-1 font-base">Avg Records/File</div>
                  </div>
                  <div className="bg-background border-2 border-border rounded-base p-4 text-center">
                    <div className="text-2xl font-heading text-foreground">
                      {iceberg.snapshots.toLocaleString()}
                    </div>
                    <div className="text-xs text-foreground/70 mt-1 font-base">Snapshots</div>
                  </div>
                </div>
                <div className="text-xs text-foreground/60 font-base">
                  {analyticsStatus?.table_path}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Vex Stats - only show when loading or data available */}
        {(loadingStatus || vex) && (
          <Card>
            <CardHeader>
              <CardTitle>Vex Stats</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingStatus ? (
                <StatsSkeleton className="grid grid-cols-2 lg:grid-cols-3 gap-4" items={6} />
              ) : (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-background border-2 border-border rounded-base p-4 text-center">
                    <div className="text-2xl font-heading text-foreground">
                      {formatCount(documentsIndexed)}
                    </div>
                    <div className="text-xs text-foreground/70 mt-1 font-base">Documents Indexed</div>
                  </div>
                  <div className="bg-background border-2 border-border rounded-base p-4 text-center">
                    <div className="text-2xl font-heading text-foreground">
                      {formatCount(vex?.index_lag)}
                    </div>
                    <div className="text-xs text-foreground/70 mt-1 font-base">Index Lag (seq)</div>
                  </div>
                  <div className="bg-background border-2 border-border rounded-base p-4 text-center">
                    <div className="text-2xl font-heading text-foreground">
                      {formatBytes(vex?.tail_bytes)}
                    </div>
                    <div className="text-xs text-foreground/70 mt-1 font-base">Unindexed WAL</div>
                  </div>
                  <div className="bg-background border-2 border-border rounded-base p-4 text-center">
                    <div className="text-2xl font-heading text-foreground">
                      {formatCount(vex?.segment_count_l0)}
                    </div>
                    <div className="text-xs text-foreground/70 mt-1 font-base">L0 Segments</div>
                  </div>
                  <div className="bg-background border-2 border-border rounded-base p-4 text-center">
                    <div className="text-2xl font-heading text-foreground">
                      {formatCount(vex?.segment_count_l1)}
                    </div>
                    <div className="text-xs text-foreground/70 mt-1 font-base">L1 Segments</div>
                  </div>
                  <div className="bg-background border-2 border-border rounded-base p-4 text-center">
                    <div className="text-2xl font-heading text-foreground">
                      {formatCount(vex?.segment_count_l2)}
                    </div>
                    <div className="text-xs text-foreground/70 mt-1 font-base">L2 Segments</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

      </div>

      {/* Custom Query */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Custom SQL Query
            <Badge variant="neutral" className="text-xs font-base">
              Query the posts table directly
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Textarea
              value={customQuery}
              onChange={(e) => setCustomQuery(e.target.value)}
              placeholder="SELECT * FROM posts LIMIT 10"
              className="font-mono text-sm h-20 resize-none"
            />
            <div className="flex items-center gap-3">
              <Button onClick={handleRunQuery} disabled={queryLoading || !customQuery.trim()}>
                <Play className="w-4 h-4" />
                {queryLoading ? 'Running...' : 'Run Query'}
              </Button>
              {queryResult && (
                <Badge variant="neutral">
                  {queryResult.row_count} row{queryResult.row_count !== 1 ? 's' : ''} returned
                </Badge>
              )}
            </div>

            {queryError && (
              <div className="p-3 bg-red-100 border-2 border-border rounded-base text-red-800 text-sm font-base">
                {queryError}
              </div>
            )}

            {queryResult && queryResult.columns.length > 0 && (
              <div className="overflow-x-auto border-2 border-border rounded-base">
                <table className="min-w-full divide-y-2 divide-border">
                  <thead className="bg-main">
                    <tr>
                      {queryResult.columns.map((col) => (
                        <th
                          key={col}
                          className="px-4 py-2 text-left text-xs font-heading text-main-foreground uppercase tracking-wider"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-secondary-background divide-y-2 divide-border">
                    {queryResult.rows.slice(0, 100).map((row, i) => (
                      <tr key={i} className="hover:bg-main/10">
                        {row.map((cell, j) => (
                          <td
                            key={j}
                            className="px-4 py-2 text-sm text-foreground font-mono whitespace-nowrap max-w-xs truncate"
                            title={String(cell)}
                          >
                            {cell === null ? (
                              <span className="text-foreground/50 italic">null</span>
                            ) : (
                              String(cell)
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {queryResult.rows.length > 100 && (
                  <div className="px-4 py-2 text-sm text-foreground/70 bg-background border-t-2 border-border font-base">
                    Showing first 100 of {queryResult.row_count} rows
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
